<script lang="ts">
	import { superForm, type Infer, type SuperValidated } from 'sveltekit-superforms';
	import LoaderCircle from '@lucide/svelte/icons/loader-circle';
	import Eye from '@lucide/svelte/icons/eye';
	import EyeOff from '@lucide/svelte/icons/eye-off';
	import MoveRight from '@lucide/svelte/icons/move-right';
	import { loginSchemaStep1, loginSchemaStep2 } from '../schemas';
	import { zod } from 'sveltekit-superforms/adapters';
	import { Button } from '$lib/components/ui/button';
	import { Input } from '$lib/components/ui/input';
	import * as Form from '$lib/components/ui/form';
	import { slide } from 'svelte/transition';
	import { cn } from '$lib/utils';

	interface Props {
		data: SuperValidated<Infer<typeof loginSchemaStep2>>;
		redirectTo?: string | undefined;
	}

	let { data, redirectTo = undefined }: Props = $props();
	let maskPassword: boolean = true;
	let passwordInputType: 'text' | 'password' = $state('password');

	const steps = [zod(loginSchemaStep1), zod(loginSchemaStep2)];
	let step = $state(1);
	let opt = $derived(steps[step - 1]);

	const form = superForm(data, {
		multipleSubmits: 'prevent',
		validators: opt,
		dataType: 'json',
		async onSubmit({ cancel, action }) {
			if (action.search === '?/login&early') return;
			if (step == steps.length) return;
			else cancel();

			const result = await validateForm({ update: true, schema: opt });
			if (result.valid) step = step + 1;
		},
		async onUpdated({ form }) {
			if (form.valid) step = 1;
		},
		validationMethod: 'onsubmit'
	});

	function handleMaskPassword() {
		if (maskPassword) {
			passwordInputType = 'text';
		} else {
			passwordInputType = 'password';
		}
		maskPassword = !maskPassword;
	}

	const { form: formData, enhance, delayed, errors, validateForm } = $state(form);
</script>

<form method="POST" action="/login?/login" use:enhance>
	<Form.Field {form} name="redirectTo">
		<Form.Control>
			{#snippet children({ props })}
				<Form.Label class="sr-only">Redirect</Form.Label>
				<input {...props} bind:value={() => redirectTo, (v) => ($formData.redirectTo = v)} hidden />
			{/snippet}
		</Form.Control>
	</Form.Field>
	{#if step === 1}
		<Form.Field {form} name="email">
			<Form.Control>
				{#snippet children({ props })}
					<div class="flex items-center justify-between">
						<Form.Label>Email Adresse</Form.Label>
						<a href="/passwort-reset" class="text-sm underline">Passwort vergessen?</a>
					</div>
					<Input {...props} autocomplete="email" bind:value={$formData.email} />
				{/snippet}
			</Form.Control>
			<div class="grid grid-cols-1 grid-rows-1">
				{#if $errors.email}
					<div
						transition:slide
						class="col-span-1 col-start-1 row-span-1 row-start-1 min-w-full max-w-min"
					>
						<Form.FieldErrors class="pb-4">
							{#snippet children({ errors, errorProps })}
								{#each errors as err}
									<span class="text-destructive transition ease-in-out" {...errorProps}>{err}</span>
								{/each}
							{/snippet}
						</Form.FieldErrors>
					</div>
				{/if}
			</div>
		</Form.Field>
		{#key $formData.password && $formData.password.length > 0}
			<Form.Field
				{form}
				name="password"
				class={cn($formData.password ? 'mb-4 grid grid-cols-1 grid-rows-1' : 'sr-only')}
			>
				<div transition:slide class="col-span-1 col-start-1 row-span-1 row-start-1">
					<Form.Control>
						{#snippet children({ props })}
							<Form.Label>Passwort</Form.Label>
							<div class="relative">
								<Input
									{...props}
									type={passwordInputType}
									autocomplete="current-password"
									bind:value={$formData.password}
								/>
								<Button
									variant="ghost"
									size="icon"
									onclick={handleMaskPassword}
									class="absolute right-0 top-0"
								>
									{#if passwordInputType === 'password'}
										<Eye class="size-5 text-muted-foreground" />
									{:else}
										<EyeOff class="size-5 text-muted-foreground" />
									{/if}
								</Button>
							</div>
							<Form.FieldErrors class="pb-4">
								{#snippet children({ errors, errorProps })}
									{#each errors as err}
										<span class="text-destructive transition ease-in-out" {...errorProps}
											>{err}</span
										>
									{/each}
								{/snippet}
							</Form.FieldErrors>
						{/snippet}
					</Form.Control>
					<div class="grid grid-cols-1 grid-rows-1">
						{#if $errors.email}
							<div
								transition:slide
								class="col-span-1 col-start-1 row-span-1 row-start-1 min-w-full max-w-min"
							>
								<Form.FieldErrors class="pb-4">
									{#snippet children({ errors, errorProps })}
										{#each errors as err}
											<span class="text-destructive transition ease-in-out" {...errorProps}
												>{err}</span
											>
										{/each}
									{/snippet}
								</Form.FieldErrors>
							</div>
						{/if}
					</div>
				</div>
			</Form.Field>
		{/key}
		{#if $formData.password}
			<Form.Button class="group w-full gap-2" formaction="/login?/login&early">
				{#if $delayed}
					<LoaderCircle class="size-5 animate-spin" />
				{:else}
					Einloggen <MoveRight class="size-5 transition ease-in-out group-hover:translate-x-1" />
				{/if}
			</Form.Button>
		{:else}
			<Form.Button class="group w-full gap-2">
				{#if $delayed}
					<LoaderCircle class="size-5 animate-spin" />
				{:else}
					Weiter <MoveRight class="size-5 transition ease-in-out group-hover:translate-x-1" />
				{/if}
			</Form.Button>
		{/if}
	{:else}
		<Form.Field {form} name="email" class="sr-only">
			<Form.Control>
				{#snippet children({ props })}
					<Form.Label>Email Adresse</Form.Label>
					<Input {...props} type="email" autocomplete="email" bind:value={$formData.email} />
				{/snippet}
			</Form.Control>
		</Form.Field>
		<Form.Field {form} name="password">
			<Form.Control>
				{#snippet children({ props })}
					<div class="flex items-center justify-between">
						<Form.Label>Passwort</Form.Label>
						<a href="/passwort-reset" class="text-sm underline">Passwort vergessen?</a>
					</div>
					<div class="relative">
						<Input
							{...props}
							type={passwordInputType}
							autocomplete="current-password"
							autofocus
							bind:value={$formData.password}
						/>
						<Button
							variant="ghost"
							size="icon"
							onclick={handleMaskPassword}
							class="absolute right-0 top-0"
						>
							{#if passwordInputType === 'password'}
								<Eye class="size-5 text-muted-foreground" />
							{:else}
								<EyeOff class="size-5 text-muted-foreground" />
							{/if}
						</Button>
					</div>
				{/snippet}
			</Form.Control>
			<div class="grid grid-cols-1 grid-rows-1">
				{#if $errors.password}
					<div transition:slide class="col-span-1 col-start-1 row-span-1 row-start-1">
						<Form.FieldErrors class="pb-4">
							{#snippet children({ errors, errorProps })}
								{#each errors as err}
									<span class="text-destructive transition ease-in-out" {...errorProps}>{err}</span>
								{/each}
							{/snippet}
						</Form.FieldErrors>
					</div>
				{/if}
			</div>
		</Form.Field>
		<Form.Button type="submit" class="group w-full gap-2">
			{#if $delayed}
				<LoaderCircle class="size-5 animate-spin" />
			{:else}
				Einloggen <MoveRight class="size-5 transition ease-in-out group-hover:translate-x-1" />
			{/if}
		</Form.Button>
	{/if}
</form>
